import _ from 'lodash';

export type RequirementValues = {
  minValue: number;
  maxValue: number;
  label: string;
};

export enum CorporateTitle {
  AccountManager = 10,
  SalesManager = 20,
  RegionalManager = 30,
  SalesDirector = 40,
  CEO = 50,
}

export enum CorporateTitleLabels {
  AccountManager = 'Asiakkuuspäällikkö',
  SalesManager = 'Myyntipäällikkö',
  RegionalManager = 'Aluepäällikkö',
  SalesDirector = 'Myyntijohtaja',
  CEO = 'Toimitusjohtaja',
}

export type AcceptanceMatrixRow<T, U, E> = {
  reason: T;
  requirements: Record<CorporateTitle, RequirementValues | undefined>;
  label: string;
  getValueForValidation: (sheet: U, rows: E[]) => ValidationValue;
  validator: (
    value: ValidationValue,
    requirements?: RequirementValues,
  ) => boolean;
  formatValue: (value: ValidationValue) => string;
};

export type ValidationValue =
  | number
  | { dayValue?: number; monthValue?: number }
  | undefined;

/**
 * Gets corporate titles as an string array
 */
export const getCorporateTitles = () =>
  Object.values(CorporateTitle).filter(
    (value) => typeof value === 'number',
  );

/**
 * Validates that a single numeric value is between range
 */
export const commonValidator = (
  value?: ValidationValue,
  requirements?: RequirementValues,
) =>
  typeof value === 'number' &&
  value !== undefined &&
  requirements !== undefined &&
  value < requirements.maxValue &&
  value >= requirements.minValue;

/**
 * Validates that dayValue and monthValue are between range separately
 */
export const commonValidatorPricingBasis = (
  value?: ValidationValue,
  requirements?: RequirementValues,
) => {
  if (!value || !requirements) {
    return false;
  }
  return (
    (typeof value === 'object' &&
      value.dayValue !== undefined &&
      value.dayValue < requirements.maxValue &&
      value.dayValue >= requirements.minValue) ||
    (typeof value === 'object' &&
      value.monthValue !== undefined &&
      value.monthValue < requirements.maxValue &&
      value.monthValue >= requirements.minValue)
  );
};

export const approvalValidatorBase = <T, U, E>(
  sheet: U,
  rows: E[],
  acceptanceMatrixRows: AcceptanceMatrixRow<T, U, E>[],
) => {
  const result = acceptanceMatrixRows.map((matrixRow) => {
    const value = matrixRow.getValueForValidation(sheet, rows);
    const requirements = Object.entries(
      matrixRow.requirements,
    ).reduce<Record<string, any>>((acc, [title, req]) => {
      const titleValue = Number(title);
      let highestLevel: number | undefined = _.get(
        acc,
        'requiredApprovalLevel',
      );
      const isRequired = matrixRow.validator(value, req);

      if (
        isRequired &&
        (!highestLevel || titleValue > highestLevel)
      ) {
        highestLevel = titleValue;
      }

      return {
        ...acc,
        requiredApprovalLevel: highestLevel,
        [title]: {
          label: req?.label,
          isRequired: isRequired,
        },
      };
    }, {});

    return {
      value: value,
      displayValue: matrixRow.formatValue(value),
      reasonLabel: matrixRow.label,
      requirements: requirements,
    };
  });

  const totalRequiredApprovalLevel = result.reduce((acc, cur) => {
    const requiredApprovalLevel =
      cur.requirements.requiredApprovalLevel;
    let highest =
      requiredApprovalLevel && !acc ? requiredApprovalLevel : acc;
    if (
      highest &&
      requiredApprovalLevel &&
      requiredApprovalLevel > highest
    ) {
      highest = requiredApprovalLevel;
    }
    return highest;
  }, undefined);

  return {
    result,
    requiredApprovalLevel: totalRequiredApprovalLevel,
    requiresApproval: Boolean(totalRequiredApprovalLevel),
  };
};
